import { alpha, hexToRgb } from '@mui/system';
import { default as LinearProgress, linearProgressClasses } from '@mui/material/LinearProgress';

import { Grid } from 'Grid';
import { Paper, PaperProps } from 'Paper';
import { Button, ButtonProps } from 'Button';
import { IconButton, IconButtonProps } from 'IconButton';
import { Typography, TypographyProps } from 'Typography';
import { Icon } from 'Icon';
import { styled } from 'helpers';
import { defaultTheme } from 'Theme';

export const snackbarColors = {
  default: {
    actionColor: defaultTheme.palette.lightBlue[400],
    backgroundColor: defaultTheme.palette.blueGrey[900],
    btnHoverBackground: 'rgba(255, 255, 255, 0.16)',
    color: defaultTheme.palette.common.white,
  },
  error: {
    actionColor: defaultTheme.palette.primary.main,
    backgroundColor: defaultTheme.palette.common.white,
    btnHoverBackground: defaultTheme.palette.grey['A50'],
    color: defaultTheme.palette.text.primary,
  },
  progress: {
    actionColor: defaultTheme.palette.lightBlue[400],
    backgroundColor: defaultTheme.palette.blueGrey[900],
    btnHoverBackground: 'rgba(255, 255, 255, 0.16)',
    color: defaultTheme.palette.common.white,
  },
};

interface StyledIconButtonProps extends IconButtonProps {
  snackVariant: 'default' | 'error' | 'progress';
}

// prettier-ignore
export const StyledIconButton = styled(IconButton,{ shouldForwardProp: (prop) => prop !== 'snackVariant'})<StyledIconButtonProps>`
  margin-top: 12px;
  margin-bottom: 12px;
  margin-left: 19px;
  margin-right: 12px;

  &:hover {
    background-color: ${(props) => snackbarColors[props.snackVariant].btnHoverBackground};
  }

  svg {
    fill: ${({ theme }) => theme.palette.blueGrey['A400']};
  }
`;

interface StyledPaperProps extends PaperProps {
  snackVariant: 'default' | 'error' | 'progress';
}

// prettier-ignore
export const StyledPaper = styled(Paper, { shouldForwardProp: (prop) => prop !== 'snackVariant' && prop !== 'showCloseIcon' })<StyledPaperProps>`
  padding-left: 24px;
  color: ${(props) => snackbarColors[props.snackVariant].color};
  background-color: ${(props) => snackbarColors[props.snackVariant].backgroundColor};
  border-radius: 8px;
  box-shadow: ${({ theme }) => `0 2px 16px ${alpha(hexToRgb(theme.palette.blueGrey[900]), 0.3)}`};
  width: max-content;
  `;

interface StyledActionProps extends ButtonProps {
  snackVariant: 'default' | 'error' | 'progress';
}

// prettier-ignore
export const StyledActionText = styled(Button, { shouldForwardProp: (prop) => prop !== 'snackVariant' })<StyledActionProps>`
  display: inline;
  margin-left: 16px;
  margin-right: -8px;
  &.MuiButton-text.MuiButton-textPrimary {
    color: ${(props) => snackbarColors[props.snackVariant].actionColor};
  }

  &.MuiButton-text.MuiButton-textPrimary:active,
  &.MuiButton-text.MuiButton-textPrimary:hover {
    background-color: unset;
  }
`;

export const StyledSnackbarGrid = styled(Grid)`
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
`;

// prettier-ignore
export const StyledErrorIcon = styled(Icon, { shouldForwardProp: (prop) => prop !== 'snackVariant' })`
  color: ${(props) =>  props.theme.palette.pink['700']};
  margin-right: 8px;
  margin-left: 4px;
  `;

interface StyledTypographyProps extends TypographyProps {
  snackVariant: 'default' | 'error' | 'progress';
}
// prettier-ignore
export const StyledTypography = styled(Typography, { shouldForwardProp: (prop) => prop !== 'snackVariant' })<StyledTypographyProps>`
  margin-top: 18px;
  margin-bottom: ${({ snackVariant }) => (snackVariant === 'progress' ? '4px' : '18px')};
  font-weight: ${({ snackVariant }) => (snackVariant === 'error' ? 'bold' : '400')};
`;

export const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  borderRadius: 2,
  marginBottom: 18,
  marginTop: 4,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.blueGrey[700],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 2,
    backgroundColor: theme.palette.lightBlue[400],
  },
}));
